/**
 * Step 8 — rules post-update (owner: rules team), after movement, ball and contacts:
 *  - PRE_SNAP: re-clamp the offense (head AND the ball in its glove) behind the LOS; OFFSIDE (defender past the
 *    LOS) → replay the down.
 *  - RUSH_COUNT: the offense (head or held ball) or its pass crossing the LOS ends the count (RUSH_GO
 *    'offense_crossed'); an interception ends it too (RUSH_GO 'turnover': the return is live at once); OFFSIDE
 *    only while the offense still has the ball (held, or its pass in the air).
 *  - RUSH_COUNT / LIVE: catches & interceptions (legality), swats, per-yard power past playBestX, and the play
 *    end — TD / pick-six, tackle (sack, safety, first down, turnover on downs), incomplete, field goal good/miss,
 *    conversions, the anti-stall whistle. PLAY_END comes after the specific result events.
 *  - every phase: new baits → power; POWER_GAIN / POWER_FULL.
 * ARCHITECTURE §5 "Football rules"; the cross-module protocol is documented in ./helpers.ts.
 */
import {
  BAIT_WINDOW_HARD_COUNT_FRAMES,
  FIELD_MAX_X,
  FIELD_MIN_X,
  GOAL_LINE_LEFT,
  GOAL_LINE_RIGHT,
  HITSTOP,
  KICK,
  KICKOFF_TOUCHBACK_YARDS_TO_GOAL,
  POINTS_FIELD_GOAL,
  POINTS_PAT_TOSS,
  POINTS_SAFETY,
  POINTS_TOUCHDOWN,
  POINTS_TWO_POINT,
  POWER,
  RULES,
  type SpecialId,
  TOUCHBACK_YARDS_TO_GOAL,
  endLineFor,
  goalLineFor,
  xFromYardsToGoal,
  yardsToGoal,
} from '@hf/shared';
import {
  AfterDead,
  BallState,
  BtnIndex,
  FEINT_BAIT_KEYS,
  FeintKind,
  Phase,
  PlayCall,
  PlayResultKind,
  PlayerState,
  PowerReason,
  SpecialPhase,
  isKickCall,
} from '../enums';
import { addHitStop, fakeSuccess } from '../contact/fx';
import { kickoffPending } from '../selectors';
import type { SimEventSink } from '../sink';
import { setOffense, type PlayerSim, type SimState } from '../state';
import { conversionCanMatter } from './flow';
import {
  FgVerdict,
  carrierAttackDir,
  classicRegulation,
  fgCrossingY,
  fgVerdict,
  inEndZone,
  isFourthDown,
  offenseCatchIsLegal,
  offenseFrontCrossed,
  offsidePenaltyYards,
  receiverOf,
} from './helpers';
import { announceYardPower, gainPower, gainYardPower, powerBegin, powerFlush, yardGain } from './power';
import { rushGoOffenseCrossed, rushGoTurnover } from './pre';

const score = (s: SimState): [number, number] => [s.rules.score[0] ?? 0, s.rules.score[1] ?? 0];

const addScore = (s: SimState, p: number, points: number): void => {
  s.rules.score[p] = (s.rules.score[p] ?? 0) + points;
};

/** Specials still telegraphing / running when the whistle blows: SPECIAL_END{consumed:false}, power stays spent. */
const endRunningSpecials = (s: SimState, ev: SimEventSink): void => {
  for (let i = 0; i < 2; i++) {
    const sp = s.players[i as 0 | 1].special;
    if (sp.phase !== SpecialPhase.TELEGRAPH && sp.phase !== SpecialPhase.ACTIVE) continue;
    if (ev)
      ev.push({ type: 'SPECIAL_END', tick: s.tick, actor: i, special: sp.id as SpecialId, consumed: false });
    sp.phase = SpecialPhase.SPENT;
    sp.frame = 0;
    sp.timer = 0;
    sp.decoyActive = false;
    sp.decoyFrames = 0;
  }
};

/** Common play-end bookkeeping: result, DEAD phase, dead ball, specials, PLAY_END (after the specific events). */
const finishPlay = (
  s: SimState,
  ev: SimEventSink,
  kind: PlayResultKind,
  actor: number,
  yards: number,
  points: number,
  scorer: number,
  spotX: number,
  firstDown: boolean,
  after: AfterDead,
): void => {
  const r = s.rules;
  const lr = r.lastResult;
  lr.kind = kind;
  lr.actor = actor;
  lr.yards = yards;
  lr.points = points;
  lr.scorer = scorer;
  lr.spotX = spotX;
  lr.firstDown = firstDown;
  r.afterDead = after;
  r.phase = Phase.DEAD;
  r.phaseFrame = 0;
  r.rushCount = 0;
  // Classic clock: only a tackle in bounds that keeps the drive going lets it run on (until the next snap), and not
  // inside the last RULES.classicClockStopsLastFrames of a half. Everything else stops it.
  r.clockRunning =
    r.clockRunning &&
    (kind === PlayResultKind.TACKLE || kind === PlayResultKind.SACK) &&
    after === AfterDead.NEXT_DOWN &&
    r.gameClock > RULES.classicClockStopsLastFrames;
  s.ball.state = BallState.DEAD;
  s.ball.kick = false;
  endRunningSpecials(s, ev);
  announceYardPower(s);
  if (ev) ev.push({ type: 'PLAY_END', tick: s.tick, actor: -1, result: kind, yards, spotX });
};

const celebrate = (s: SimState, ev: SimEventSink, p: number): void => {
  const pl = s.players[p as 0 | 1];
  pl.prevState = pl.state;
  pl.state = PlayerState.CELEBRATE;
  pl.stateFrame = 0;
  if (ev) ev.push({ type: 'CELEBRATE', tick: s.tick, actor: p, x: pl.x, y: pl.y });
};

const conversionResult = (s: SimState, ev: SimEventSink, good: boolean, points: number): void => {
  if (ev) {
    ev.push({
      type: 'CONVERSION_RESULT',
      tick: s.tick,
      actor: s.rules.offense,
      good,
      points,
      score: score(s),
    });
  }
};

/** The try failed (tackled short, incomplete, missed toss, intercepted…). */
const conversionFailed = (s: SimState, ev: SimEventSink, spotX: number): void => {
  conversionResult(s, ev, false, 0);
  finishPlay(
    s,
    ev,
    PlayResultKind.CONVERSION_FAILED,
    s.rules.offense,
    0,
    0,
    -1,
    spotX,
    false,
    AfterDead.NEXT_DRIVE,
  );
};

const creditTackle = (s: SimState, tackler: number): void => {
  if (tackler !== 0 && tackler !== 1) return;
  s.players[tackler].statTackles++;
  gainPower(s, tackler, POWER.tackle, PowerReason.TACKLE);
};

/** Scrimmage yards for the offense: through the air to the catch spot, the rest on the ground. */
const creditOffenseYards = (s: SimState, spotX: number): void => {
  const r = s.rules;
  const p = s.players[r.offense as 0 | 1];
  if (r.completion) {
    p.statPassYards += (r.catchX - r.los) * r.attackDir;
    p.statYards += (spotX - r.catchX) * r.attackDir;
  } else {
    p.statYards += (spotX - r.los) * r.attackDir;
  }
};

const bigPlay = (s: SimState, ev: SimEventSink, yards: number): void => {
  if (ev && yards >= RULES.bigPlayYards) {
    ev.push({ type: 'BIG_PLAY', tick: s.tick, actor: s.rules.offense, yards });
  }
};

// ── Play-end outcomes ──────────────────────────────────────────────────────────────────────────────────

/** Offense ball carrier down at `x` (tackled by `tackler`, or -1 for the anti-stall whistle). */
const offenseDown = (s: SimState, ev: SimEventSink, carrier: PlayerSim, tackler: number): void => {
  const r = s.rules;
  const dir = r.attackDir;
  const x = carrier.x;
  creditTackle(s, tackler);
  if (r.conversionPlay) {
    conversionFailed(s, ev, x);
    return;
  }
  const yards = (x - r.los) * dir;
  creditOffenseYards(s, x);
  const def = 1 - r.offense;
  if (inEndZone(x, -dir)) {
    addScore(s, def, POINTS_SAFETY);
    if (ev) ev.push({ type: 'SAFETY', tick: s.tick, actor: def, x, y: carrier.y, score: score(s) });
    finishPlay(s, ev, PlayResultKind.SAFETY, def, yards, POINTS_SAFETY, def, x, false, AfterDead.NEXT_DRIVE);
    return;
  }
  const sack = tackler >= 0 && !r.completion && !carrier.crossedLos && yards < 0;
  if (sack) {
    s.players[tackler as 0 | 1].statSacks++;
    if (ev) ev.push({ type: 'SACK', tick: s.tick, actor: tackler, x, y: carrier.y, yards });
  }
  bigPlay(s, ev, yards);
  const firstDown = (x - r.firstDownX) * dir >= 0;
  if (firstDown) {
    if (ev) ev.push({ type: 'FIRST_DOWN', tick: s.tick, actor: r.offense, los: x });
    finishPlay(
      s,
      ev,
      sack ? PlayResultKind.SACK : PlayResultKind.TACKLE,
      tackler,
      yards,
      0,
      -1,
      x,
      true,
      AfterDead.NEXT_DOWN,
    );
  } else if (isFourthDown(s)) {
    if (ev) ev.push({ type: 'TURNOVER_ON_DOWNS', tick: s.tick, actor: r.offense });
    finishPlay(
      s,
      ev,
      PlayResultKind.TURNOVER_ON_DOWNS,
      tackler,
      yards,
      0,
      -1,
      x,
      false,
      AfterDead.NEXT_DRIVE,
    );
  } else {
    finishPlay(
      s,
      ev,
      sack ? PlayResultKind.SACK : PlayResultKind.TACKLE,
      tackler,
      yards,
      0,
      -1,
      x,
      false,
      AfterDead.NEXT_DOWN,
    );
  }
};

/** Interception returner down (or whistled): the drive is over; a stop in his own end zone is a touchback. */
const returnerDown = (s: SimState, ev: SimEventSink, carrier: PlayerSim, tackler: number): void => {
  const r = s.rules;
  creditTackle(s, tackler);
  carrier.statYards += (carrier.x - r.catchX) * -r.attackDir;
  finishPlay(
    s,
    ev,
    PlayResultKind.INTERCEPTION,
    carrier.index,
    0,
    0,
    -1,
    carrier.x,
    false,
    AfterDead.NEXT_DRIVE,
  );
};

const touchdown = (s: SimState, ev: SimEventSink, carrier: PlayerSim): void => {
  const r = s.rules;
  const c = carrier.index;
  const dir = carrierAttackDir(s, c);
  const goal = goalLineFor(dir);
  const isOffense = c === r.offense;
  const kickReturn = isKickCall(r.playCall);
  // Shares the contact team's per-(tick, actor) HIT_STOP fold (a catch in the end zone becomes one freeze).
  addHitStop(s, ev, c, HITSTOP.touchdown, 'touchdown', carrier.x, carrier.y);
  if (isOffense && r.conversionPlay) {
    // A try carried/caught into the end zone is worth 2 (also a run-in from the PAT formation).
    addScore(s, c, POINTS_TWO_POINT);
    conversionResult(s, ev, true, POINTS_TWO_POINT);
    celebrate(s, ev, c);
    finishPlay(
      s,
      ev,
      PlayResultKind.CONVERSION_GOOD,
      c,
      (goal - r.los) * dir,
      POINTS_TWO_POINT,
      c,
      goal,
      false,
      AfterDead.NEXT_DRIVE,
    );
    return;
  }
  addScore(s, c, POINTS_TOUCHDOWN);
  carrier.statTouchdowns++;
  let yards = 0;
  if (isOffense) {
    yards = (goal - r.los) * dir;
    creditOffenseYards(s, goal);
  } else {
    carrier.statYards += (goal - r.catchX) * dir;
  }
  if (ev) {
    ev.push({
      type: 'TOUCHDOWN',
      tick: s.tick,
      actor: c,
      x: carrier.x,
      y: carrier.y,
      pickSix: !isOffense && !kickReturn,
      kickReturn,
      score: score(s),
    });
  }
  if (isOffense) bigPlay(s, ev, yards);
  celebrate(s, ev, c);
  // Classic regulation: a return TD (interception or kick) earns the scorer the try like any TD — he takes the
  // ball (attack direction + wind) for it, then kicks off. Drives / overtime: no try after a defensive score.
  const returnTry = !isOffense && classicRegulation(s);
  if (returnTry) setOffense(s, c);
  const after =
    !isOffense && !returnTry
      ? AfterDead.NEXT_DRIVE
      : conversionCanMatter(s)
        ? AfterDead.CONVERSION
        : AfterDead.GAME_OVER;
  finishPlay(
    s,
    ev,
    isOffense || kickReturn ? PlayResultKind.TOUCHDOWN : PlayResultKind.PICK_SIX,
    c,
    yards,
    POINTS_TOUCHDOWN,
    c,
    goal,
    false,
    after,
  );
};

const fgDistance = (s: SimState): number => {
  const r = s.rules;
  const from = s.ball.thrownBy >= 0 ? s.ball.releaseX : r.los;
  return Math.abs(endLineFor(r.attackDir) - from);
};

const fieldGoalGood = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  const off = r.offense;
  const pat = r.conversionPlay;
  const points = pat ? POINTS_PAT_TOSS : POINTS_FIELD_GOAL;
  addScore(s, off, points);
  if (ev) {
    ev.push({
      type: 'FIELD_GOAL_GOOD',
      tick: s.tick,
      actor: off,
      x: s.ball.x,
      y: s.ball.y,
      distance: fgDistance(s),
      pat,
      score: score(s),
    });
  }
  if (pat) conversionResult(s, ev, true, points);
  celebrate(s, ev, off);
  finishPlay(
    s,
    ev,
    pat ? PlayResultKind.CONVERSION_GOOD : PlayResultKind.FIELD_GOAL_GOOD,
    off,
    0,
    points,
    off,
    r.los,
    false,
    AfterDead.NEXT_DRIVE,
  );
};

const fieldGoalMiss = (s: SimState, ev: SimEventSink, reason: 'short' | 'over' | 'post' | 'wide'): void => {
  const r = s.rules;
  const pat = r.conversionPlay;
  if (ev) {
    ev.push({
      type: 'FIELD_GOAL_MISS',
      tick: s.tick,
      actor: r.offense,
      x: s.ball.x,
      y: s.ball.y,
      distance: fgDistance(s),
      pat,
      reason,
    });
  }
  if (pat) {
    conversionFailed(s, ev, r.los);
    return;
  }
  finishPlay(s, ev, PlayResultKind.FIELD_GOAL_MISS, r.offense, 0, 0, -1, r.los, false, AfterDead.NEXT_DRIVE);
};

const incomplete = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  const b = s.ball;
  if (ev) {
    ev.push({
      type: 'INCOMPLETE',
      tick: s.tick,
      actor: b.thrownBy >= 0 ? b.thrownBy : r.offense,
      x: b.x,
      y: b.y,
    });
  }
  if (r.conversionPlay) {
    conversionFailed(s, ev, r.los);
  } else if (isFourthDown(s)) {
    if (ev) ev.push({ type: 'TURNOVER_ON_DOWNS', tick: s.tick, actor: r.offense });
    finishPlay(
      s,
      ev,
      PlayResultKind.TURNOVER_ON_DOWNS,
      r.offense,
      0,
      0,
      -1,
      r.los,
      false,
      AfterDead.NEXT_DRIVE,
    );
  } else {
    finishPlay(s, ev, PlayResultKind.INCOMPLETE, r.offense, 0, 0, -1, r.los, false, AfterDead.NEXT_DOWN);
  }
};

const offside = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  const dir = r.attackDir;
  const defIdx = 1 - r.offense;
  const def = s.players[defIdx as 0 | 1];
  const off = s.players[r.offense as 0 | 1];
  const pen = offsidePenaltyYards(r.los, dir); // never past the RULES.minLosYardsToGoal spot
  const newLos = r.los + dir * pen;
  const firstDown = !r.conversionPlay && (newLos - r.firstDownX) * dir >= 0;
  if (ev) ev.push({ type: 'OFFSIDE', tick: s.tick, actor: defIdx, x: def.x, y: def.y, penaltyYards: pen });
  // Hard-count bait: the defender jumped the snap within the bait window of the fake call.
  // (FAKE_SUCCESS + statBaits → +POWER.bait in convertBaits + the short 'fake' freeze, via the shared helper.)
  if (off.feintKind === FeintKind.HARD_COUNT && s.tick - off.feintTick <= BAIT_WINDOW_HARD_COUNT_FRAMES) {
    fakeSuccess(s, ev, off, def, FEINT_BAIT_KEYS[FeintKind.HARD_COUNT]);
  }
  if (firstDown && ev) ev.push({ type: 'FIRST_DOWN', tick: s.tick, actor: r.offense, los: newLos });
  // The ball is dead where it is (still in the QB's glove): no flight to continue.
  s.ball.vx = 0;
  s.ball.vy = 0;
  finishPlay(s, ev, PlayResultKind.OFFSIDE, defIdx, pen, 0, -1, newLos, firstDown, AfterDead.REPLAY_DOWN);
};

// ── Per-phase logic ────────────────────────────────────────────────────────────────────────────────────

/**
 * Pre-snap safety net: neither the offense's head centre nor the ball in its glove may pass the LOS (integrate
 * clamps the same front first, predicting the ball from the glove).
 */
const clampOffense = (s: SimState): void => {
  const r = s.rules;
  const p = s.players[r.offense as 0 | 1];
  const b0 = s.ball;
  const head = (p.x - r.los) * r.attackDir;
  // Math.max keeps `over` a raw double (no call returns a number on this per-tick path)
  const over =
    b0.state === BallState.HELD && b0.holder === r.offense
      ? Math.max(head, (b0.x - r.los) * r.attackDir)
      : head;
  if (over <= 0) return;
  p.x -= over * r.attackDir;
  if (p.vx * r.attackDir > 0) p.vx = 0;
  const b = s.ball;
  if (b.holder === r.offense && b.state === BallState.HELD) b.x -= over * r.attackDir;
};

const defenderOffside = (s: SimState): boolean =>
  (s.players[(1 - s.rules.offense) as 0 | 1].x - s.rules.los) * s.rules.attackDir < -RULES.offsideTolerance;

const markCrossings = (s: SimState): void => {
  const r = s.rules;
  const off = s.players[r.offense as 0 | 1];
  const def = s.players[(1 - r.offense) as 0 | 1];
  if ((off.x - r.los) * r.attackDir > 0) off.crossedLos = true;
  if ((def.x - r.los) * r.attackDir < 0) def.crossedLos = true;
};

/**
 * The carrier (his head, or the ball in his glove) broke the goal-line plane he attacks (`dir`). Per tick while the
 * ball is carried: written inline (no double crosses a call), unlike `inEndZone(x, dir)`.
 */
const carrierInEndZone = (s: SimState, pl: PlayerSim, dir: number): boolean => {
  const goal = dir > 0 ? GOAL_LINE_RIGHT : GOAL_LINE_LEFT;
  return (s.ball.x - goal) * dir >= 0 || (pl.x - goal) * dir >= 0;
};

// ── Classic kicks (ARCHITECTURE §5 "Classic format") ───────────────────────────────────────────────────

const KICK_RESULT_YARDS = 0;

/** The receiver's own end zone is the one the kicker attacks. */
const inReceiverEndZone = (s: SimState, x: number): boolean => inEndZone(x, s.rules.attackDir);

/** Where the receiver starts after a touchback (kickoff: his 25; punt: his 20). */
const touchbackSpot = (s: SimState): number => {
  const r = s.rules;
  const ytg = r.playCall === PlayCall.PUNT ? TOUCHBACK_YARDS_TO_GOAL : KICKOFF_TOUCHBACK_YARDS_TO_GOAL;
  return xFromYardsToGoal(ytg, -r.attackDir);
};

const touchback = (s: SimState, ev: SimEventSink): void => {
  const recv = receiverOf(s);
  const spot = touchbackSpot(s);
  const b = s.ball;
  if (b.holder < 0) {
    b.vx = 0;
    b.vy = 0;
  }
  if (ev) ev.push({ type: 'TOUCHBACK', tick: s.tick, actor: recv, los: spot });
  finishPlay(
    s,
    ev,
    PlayResultKind.TOUCHBACK,
    recv,
    KICK_RESULT_YARDS,
    0,
    -1,
    spot,
    false,
    AfterDead.NEXT_DRIVE,
  );
};

/** The kick died untouched by the receiver (or nobody picked it up in time): the receiver's ball at `x`. */
const kickDowned = (s: SimState, ev: SimEventSink, x: number): void => {
  if (inReceiverEndZone(s, x)) {
    touchback(s, ev);
    return;
  }
  const b = s.ball;
  b.vx = 0;
  b.vy = 0;
  b.angVel = 0;
  finishPlay(s, ev, PlayResultKind.KICK_DOWNED, receiverOf(s), 0, 0, -1, x, false, AfterDead.NEXT_DRIVE);
};

/**
 * Someone possessed the kick this tick (caught it, or picked it up). The receiver: a live return. The kicker: the
 * play ends here — a TD in the end zone he attacks, an untouched punt DOWNED (the receiver's ball), else his
 * recovery (a free kick or a muff: he keeps it; a punt keeps it only past the line to gain — else turnover on
 * downs). Returns true if the play ended.
 */
const kickPossessed = (s: SimState, ev: SimEventSink, pl: PlayerSim): boolean => {
  const r = s.rules;
  const b = s.ball;
  if (pl.index !== r.offense) {
    r.catchX = pl.x;
    pl.playBestX = pl.x;
    return false;
  }
  const x = pl.x;
  if (inEndZone(x, r.attackDir) || inEndZone(b.x, r.attackDir)) {
    touchdown(s, ev, pl);
    return true;
  }
  const receiverTouched = (b.touchedMask & (1 << receiverOf(s))) !== 0;
  b.vx = 0;
  b.vy = 0;
  b.angVel = 0;
  if (r.playCall === PlayCall.PUNT && !receiverTouched) {
    kickDowned(s, ev, x);
    return true;
  }
  if (r.playCall === PlayCall.PUNT && (x - r.firstDownX) * r.attackDir < 0) {
    // a muffed / blocked punt recovered short of the line to gain: still 4th down
    if (ev) ev.push({ type: 'TURNOVER_ON_DOWNS', tick: s.tick, actor: r.offense });
    finishPlay(s, ev, PlayResultKind.TURNOVER_ON_DOWNS, r.offense, 0, 0, -1, x, false, AfterDead.NEXT_DRIVE);
    return true;
  }
  finishPlay(
    s,
    ev,
    PlayResultKind.KICK_RECOVERED,
    r.offense,
    (x - r.los) * r.attackDir,
    0,
    -1,
    x,
    r.playCall === PlayCall.PUNT,
    AfterDead.NEXT_DRIVE,
  );
  return true;
};

/** The kick returner (the receiver, carrying) is down / whistled / takes a knee at his spot. */
const kickReturnerDown = (s: SimState, ev: SimEventSink, carrier: PlayerSim, tackler: number): void => {
  const r = s.rules;
  creditTackle(s, tackler);
  carrier.statYards += (carrier.x - r.catchX) * -r.attackDir;
  if (inReceiverEndZone(s, carrier.x)) {
    touchback(s, ev);
    return;
  }
  const b = s.ball;
  b.vx = 0;
  b.vy = 0;
  b.angVel = 0;
  finishPlay(
    s,
    ev,
    PlayResultKind.KICK_RETURN,
    carrier.index,
    (carrier.x - r.catchX) * -r.attackDir,
    0,
    -1,
    carrier.x,
    false,
    AfterDead.NEXT_DRIVE,
  );
};

/** A kick returner in his own end zone holding ▼ (KICK.kneelHoldFrames) takes a knee: touchback. */
const kneels = (s: SimState, p: PlayerSim): boolean =>
  p.grounded &&
  (p.input.held[BtnIndex.D] ?? 0) >= KICK.kneelHoldFrames &&
  carrierInEndZone(s, p, s.rules.attackDir);

/** Play end on a kick play once the toss is away (Classic). */
const kickPlayEnd = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  const b = s.ball;
  if (b.holder >= 0 && (b.state === BallState.HELD || b.state === BallState.DEAD)) {
    // the returner (the kicker's recoveries end the play in kickPossessed)
    const pl = s.players[b.holder as 0 | 1];
    const dir = carrierAttackDir(s, pl.index);
    if (carrierInEndZone(s, pl, dir)) {
      touchdown(s, ev, pl);
      return;
    }
    const downed = b.state === BallState.DEAD || pl.state === PlayerState.TACKLED;
    if (downed) kickReturnerDown(s, ev, pl, 1 - pl.index);
    else if (kneels(s, pl) || s.tick - r.snapTick >= RULES.maxLiveFrames) kickReturnerDown(s, ev, pl, -1);
    return;
  }
  const out = b.x < FIELD_MIN_X || b.x > FIELD_MAX_X;
  if (b.state === BallState.AIR) {
    // out of the back of either end zone in the air: touchback
    if (out) touchback(s, ev);
    else if (stalled(s)) kickDowned(s, ev, b.x);
    return;
  }
  // LOOSE (or a dead ball nobody holds)
  const receiverTouched = (b.touchedMask & (1 << receiverOf(s))) !== 0;
  if (out || (!receiverTouched && inReceiverEndZone(s, b.x))) {
    touchback(s, ev);
    return;
  }
  if (b.state !== BallState.LOOSE || b.looseFrames >= KICK.looseDeadFrames || stalled(s))
    kickDowned(s, ev, b.x);
};

/** Catch / interception on this tick (ball AIR → HELD). Returns true if it ended the play. */
const handleCatch = (s: SimState, ev: SimEventSink): boolean => {
  const r = s.rules;
  const b = s.ball;
  const was = r.prevBallState;
  if ((was !== BallState.AIR && was !== BallState.LOOSE) || b.state !== BallState.HELD || b.holder < 0)
    return false;
  const c = b.holder;
  const pl = s.players[c as 0 | 1];
  if (isKickCall(r.playCall) && r.passThrown) return kickPossessed(s, ev, pl);
  if (c === r.offense) {
    if (!offenseCatchIsLegal(s, c)) {
      // Caught behind the LOS (or out the back): incomplete, the ball drops dead.
      b.state = BallState.DEAD;
      b.holder = -1;
      incomplete(s, ev);
      return true;
    }
    r.completion = true;
    r.catchX = pl.x;
    pl.statCatches++;
    pl.playBestX = pl.x;
    gainPower(s, c, POWER.catch, PowerReason.CATCH);
    return false;
  }
  r.catchX = pl.x;
  pl.statInterceptions++;
  pl.playBestX = pl.x;
  gainPower(s, c, POWER.interception, PowerReason.INTERCEPTION);
  if (r.conversionPlay) {
    // No defensive return on a try: the conversion simply fails.
    conversionFailed(s, ev, r.los);
    return true;
  }
  return false;
};

/** Swat / breakup: a new touch bit for a non-thrower who did not end up holding the ball. */
const handleSwats = (s: SimState): void => {
  const b = s.ball;
  const fresh = b.touchedMask & ~s.rules.prevTouchedMask;
  if (fresh === 0) return;
  // a kick: only a block at the line pays (a receiver's muff downfield does not)
  if (b.kick && b.passedLos) return;
  for (let i = 0; i < 2; i++) {
    if (((fresh >> i) & 1) === 0 || i === b.thrownBy) continue;
    if (b.state === BallState.HELD && b.holder === i) continue;
    gainPower(s, i, POWER.swat, PowerReason.SWAT);
  }
};

/** Power for NEW progress by the carrier past playBestX (capped at the goal line). */
const yardProgress = (s: SimState): void => {
  const b = s.ball;
  // HELD, or DEAD in the carrier's glove on the tackle tick (the yards up to the spot still count).
  if (b.holder < 0 || (b.state !== BallState.HELD && b.state !== BallState.DEAD)) return;
  const pl = s.players[b.holder as 0 | 1];
  const dir = carrierAttackDir(s, b.holder);
  const goal = goalLineFor(dir);
  // capped at the goal line; Math.min/max keeps x a raw double (a `? goal : x` merge would box it every tick)
  const x = dir > 0 ? Math.min(pl.x, goal) : Math.max(pl.x, goal);
  const progress = (x - pl.playBestX) * dir;
  if (progress <= 0) return;
  // (through scratch: a double passed to a call the optimizer does not inline would be boxed every tick)
  yardGain.amount = progress * POWER.perYard;
  gainYardPower(s, b.holder);
  pl.playBestX = x;
};

/** A flight still up long after the anti-stall whistle would have blown (cannot happen with sane physics). */
const stalled = (s: SimState): boolean => s.tick - s.rules.snapTick >= 2 * RULES.maxLiveFrames;

/** Detect the end of a live play. */
const detectPlayEnd = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  const b = s.ball;
  if (isKickCall(r.playCall) && r.passThrown) {
    kickPlayEnd(s, ev);
    return;
  }
  if (b.holder >= 0 && (b.state === BallState.HELD || b.state === BallState.DEAD)) {
    const c = b.holder;
    const pl = s.players[c as 0 | 1];
    const dir = carrierAttackDir(s, c);
    if (carrierInEndZone(s, pl, dir)) {
      touchdown(s, ev, pl);
      return;
    }
    const downed = b.state === BallState.DEAD || pl.state === PlayerState.TACKLED;
    const whistle = !downed && s.tick - r.snapTick >= RULES.maxLiveFrames;
    if (!downed && !whistle) return;
    const tackler = downed ? 1 - c : -1;
    b.vx = 0;
    b.vy = 0;
    b.angVel = 0;
    if (c === r.offense) offenseDown(s, ev, pl, tackler);
    else returnerDown(s, ev, pl, tackler);
    return;
  }
  if (
    b.fgAttempt &&
    (b.state === BallState.AIR || b.state === BallState.DEAD || b.state === BallState.LOOSE)
  ) {
    if ((b.x - endLineFor(r.attackDir)) * r.attackDir > 0) {
      // Past the end-line plane (this tick, or it went dead out there before the check saw it): judge the crossing.
      const v = fgVerdict(fgCrossingY(b.x, b.y, b.vx, b.vy, r.attackDir));
      if (v === FgVerdict.GOOD) fieldGoalGood(s, ev);
      else fieldGoalMiss(s, ev, v === FgVerdict.OVER ? 'over' : 'wide');
    } else if (b.state !== BallState.AIR || b.x < FIELD_MIN_X || b.x > FIELD_MAX_X || stalled(s)) {
      fieldGoalMiss(s, ev, b.bounces > 0 ? 'post' : 'short');
    }
    return;
  }
  if (b.state === BallState.DEAD || b.state === BallState.LOOSE) {
    // Ground contact (LOOSE is reserved for onside tosses; in a scrimmage play a grounded ball is dead).
    b.state = BallState.DEAD;
    b.holder = -1;
    incomplete(s, ev);
    return;
  }
  if (b.state === BallState.AIR) {
    // Out of the back of either end zone = out of bounds. Also a ball that never comes down (should not happen).
    if (b.x > FIELD_MAX_X || b.x < FIELD_MIN_X || stalled(s)) {
      b.state = BallState.DEAD;
      b.holder = -1;
      incomplete(s, ev);
    }
  }
};

const livePlay = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  if (r.phase === Phase.RUSH_COUNT) {
    const b = s.ball;
    const offenseHolds = b.state === BallState.HELD && b.holder === r.offense;
    const passCrossed = b.state === BallState.AIR && b.forwardPass && (b.x - r.los) * r.attackDir > 0;
    if (b.state === BallState.HELD && b.holder >= 0 && !offenseHolds) {
      // Interception behind the LOS: the return is live now (contacts run, nobody can be offside any more).
      rushGoTurnover(s, ev);
    } else if ((offenseHolds && offenseFrontCrossed(s)) || passCrossed) {
      rushGoOffenseCrossed(s, ev);
    } else if ((offenseHolds || b.state === BallState.AIR) && defenderOffside(s)) {
      // Only while the offense still has the ball (in hand or its pass in the air): a dead ball is incomplete.
      offside(s, ev);
      return;
    }
  }
  markCrossings(s);
  if (handleCatch(s, ev)) return;
  handleSwats(s);
  yardProgress(s);
  detectPlayEnd(s, ev);
};

/** Convert baits detected by any module (statBaits increments) into power. */
const convertBaits = (s: SimState): void => {
  const counted = s.rules.baitsCounted;
  for (let i = 0; i < 2; i++) {
    const p = s.players[i as 0 | 1];
    const fresh = p.statBaits - (counted[i] ?? 0);
    if (fresh > 0) gainPower(s, i, POWER.bait * fresh, PowerReason.BAIT);
    counted[i] = p.statBaits;
  }
};

export const rulesPost = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  powerBegin(s);
  switch (r.phase) {
    case Phase.PRE_SNAP:
      clampOffense(s);
      if (defenderOffside(s)) offside(s, ev);
      break;
    case Phase.RUSH_COUNT:
    case Phase.LIVE:
      // the kickoff kicker stays behind the LOS until the toss is away (like pre-snap)
      if (kickoffPending(s)) clampOffense(s);
      livePlay(s, ev);
      break;
    default:
  }
  convertBaits(s);
  r.prevBallState = s.ball.state;
  r.prevTouchedMask = s.ball.touchedMask;
  powerFlush(s, ev);
};
